import { useEffect, useRef, useState } from "react";
import { ToastContainer } from "react-toastify";

import styles from "./App.module.css";
import { Banner } from "./components/Banner.js";
import { HeaterList } from "./components/HeaterList.js";
import { InstallPrompt } from "./components/InstallPrompt.js";
import { logout } from "./ha/connection.js";
import { useConnection } from "./ha/hooks.js";
import { Spinner } from "./components/ui/Spinner.js";

const PAGE_TITLE = import.meta.env.VITE_APP_NAME ?? "Heater Control";

// A reconnect usually succeeds within a second or two, so hold back the
// "something is wrong" messaging until the trouble has actually lasted.
const TROUBLE_DELAY_MS = 5000;

export default function App() {
  const { connection, entities, user, status } = useConnection();
  const errorRef = useRef<HTMLDialogElement>(null);
  const [showTrouble, setShowTrouble] = useState(false);

  useEffect(() => {
    document.title = PAGE_TITLE;
  }, []);

  useEffect(() => {
    if (status === "error" || status === "reconnecting") {
      errorRef.current?.showModal();
    } else {
      errorRef.current?.close();
    }
  }, [status]);

  const isErrored = status === "error" || status === "reconnecting";

  useEffect(() => {
    if (!isErrored) {
      setShowTrouble(false);
      return;
    }
    const id = setTimeout(() => {
      setShowTrouble(true);
    }, TROUBLE_DELAY_MS);
    return () => {
      clearTimeout(id);
    };
  }, [isErrored]);

  const isLoaded = Object.values(entities).length > 0;

  const logoutInternal =
    connection == null
      ? undefined
      : () => {
          void logout();
        };

  return (
    <div className={styles.app}>
      <Banner
        title={PAGE_TITLE}
        username={user?.name ?? null}
        onLogout={logoutInternal}
      />
      <main className={styles.main}>
        <dialog ref={errorRef}>
          <div className={styles.errorBody}>
            <h2 className={styles.errorTitle}>
              Reconnecting to Home Assistant
            </h2>
            <div className={styles.errorIcon}>
              <Spinner />
            </div>
            {showTrouble && (
              <>
                <p>We're having trouble connecting.</p>
                <button
                  type="button"
                  onClick={() => {
                    void logout().then(() => {
                      window.location.reload();
                    });
                  }}
                  className={styles.reloadButton}
                >
                  Logout & Reload
                </button>
              </>
            )}
          </div>
        </dialog>
        {status !== "error" && !connection && (
          <div className={styles.loading}>Connecting&hellip;</div>
        )}
        {!isLoaded && connection && (
          <div className={styles.loading}>Connecting&hellip;</div>
        )}
        {isLoaded && connection && (
          <HeaterList
            connection={connection}
            entities={entities}
            username={user?.name || "Pilot"}
          />
        )}
      </main>
      <InstallPrompt />
      {import.meta.env.VITE_REPO_URL != null && (
        <div className={styles.codeLink}>
          <a
            href={import.meta.env.VITE_REPO_URL}
            target="_blank"
            rel="noopener noreferer"
          >
            <img src="github.png" alt="View code on GitHub" />
          </a>
        </div>
      )}
      <ToastContainer position="top-center" />
    </div>
  );
}
